import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getQuestionTypeIcon } from '@/features/questionnaires';
import type { Question } from '@aphp-ema/common/questionnaire';
import type { QuestionConditionStats } from '@/features/questionnaires';

interface DependenciesTabProps {
  questionStats?: QuestionConditionStats;
  allQuestions?: Question[];
  currentQuestionIndex: number;
  currentQuestionLabel?: string;
}

export const DependenciesTab: React.FC<DependenciesTabProps> = ({
  questionStats,
  allQuestions,
  currentQuestionIndex,
  currentQuestionLabel,
}) => {
  const { t } = useTranslation();

  if (!questionStats || !allQuestions) {
    return null;
  }

  const questionDisplay =
    currentQuestionLabel ||
    `${t('pages.studies.questions.title', { index: currentQuestionIndex + 1 })}`;

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('pages.studies.questions.dependentQuestions.description', {
          question: questionDisplay,
          count: questionStats.dependentQuestionsCount,
        })}
      </Typography>

      {questionStats.dependentQuestionIds.length > 0 ? (
        <List>
          {questionStats.dependentQuestionIds.map((questionId) => {
            const dependentQuestion = allQuestions.find(
              (q) => q.id === questionId,
            );
            const dependentIndex = allQuestions.findIndex(
              (q) => q.id === questionId,
            );
            if (!dependentQuestion) return null;

            return (
              <ListItem key={questionId}>
                <ListItemIcon>
                  {getQuestionTypeIcon(dependentQuestion.parameters.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    dependentQuestion.label ||
                    `${t('pages.studies.questions.title', { index: dependentIndex + 1 })} - ${t('pages.studies.questions.conditions.noLabelYet')}`
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        'pages.studies.questions.dependentQuestions.position',
                        { position: dependentIndex + 1 },
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
          {t('pages.studies.questions.dependentQuestions.noDependents')}
        </Typography>
      )}

      <Box
        sx={{
          mt: 3,
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <strong>
            {t('pages.studies.questions.dependentQuestions.note')}:
          </strong>{' '}
          {t('pages.studies.questions.dependentQuestions.noteDescription')}
        </Typography>
      </Box>
    </Box>
  );
};
